import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { api } from '../../services/api';
import { clientsApi } from '../../services/clients';
import { casesApi } from '../../services/cases';
import { Advocate } from '../../types';

interface AdvocateListResponse {
  advocates: Advocate[];
  totalCount: number;
}

export function TenantDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    advocateCount: 0,
    activeAdvocates: 0,
    totalClients: 0,
    activeClients: 0,
    totalCases: 0,
    activeCases: 0,
  });

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [advocatesData, clientsData, casesData] = await Promise.all([
        api.get<AdvocateListResponse>('/tenant/advocates'),
        clientsApi.list(),
        casesApi.list(),
      ]);

      setStats({
        advocateCount: advocatesData.totalCount,
        activeAdvocates: advocatesData.advocates.filter(a => a.status === 'active').length,
        totalClients: clientsData.count,
        activeClients: clientsData.clients.filter(c => c.status === 'active').length,
        totalCases: casesData.count,
        activeCases: casesData.cases.filter(c => c.status === 'active').length,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0 }}>Tenant Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => navigate('/clients/new')}
            style={{ backgroundColor: '#1976d2', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}
          >
            + New Client
          </button>
          <button
            onClick={() => navigate('/tenant/advocates')}
            style={{ backgroundColor: '#4caf50', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '4px', cursor: 'pointer' }}
          >
            Manage Advocates
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
        <DashboardCard
          title="Advocates"
          value={loading ? '...' : stats.advocateCount.toString()}
          color="#1a237e"
          link="/tenant/advocates"
          linkText="Manage"
        />
        <DashboardCard
          title="Total Clients"
          value={loading ? '...' : stats.totalClients.toString()}
          color="#2196f3"
          link="/clients"
          linkText="View All"
        />
        <DashboardCard
          title="Active Clients"
          value={loading ? '...' : stats.activeClients.toString()}
          color="#4caf50"
        />
        <DashboardCard
          title="Active Cases"
          value={loading ? '...' : stats.activeCases.toString()}
          color="#ff9800"
        />
      </div>

      <div style={{ marginTop: '32px', backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        <h2 style={{ marginBottom: '16px', fontSize: '18px' }}>Practice Overview</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
          <div>
            <p style={{ color: '#666', fontSize: '14px' }}>Total Cases</p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '4px 0' }}>{loading ? '...' : stats.totalCases}</p>
          </div>
          <div>
            <p style={{ color: '#666', fontSize: '14px' }}>Active Advocates</p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '4px 0' }}>{loading ? '...' : stats.activeAdvocates}</p>
          </div>
          <div>
            <p style={{ color: '#666', fontSize: '14px' }}>Billing (Phase 5)</p>
            <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '4px 0', color: '#9e9e9e' }}>—</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

interface DashboardCardProps {
  title: string;
  value: string;
  color?: string;
  link?: string;
  linkText?: string;
}

function DashboardCard({ title, value, color = '#1a237e', link, linkText }: DashboardCardProps) {
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
      <h3 style={{ color: '#666', fontSize: '14px', marginBottom: '8px' }}>{title}</h3>
      <p style={{ fontSize: '36px', fontWeight: 'bold', color, margin: '0 0 8px' }}>{value}</p>
      {link && <Link to={link} style={{ color: '#1a237e', fontSize: '14px' }}>{linkText} →</Link>}
    </div>
  );
}
